import { endOfMoscowDay, formatServerDate, parseServerDate, startOfMoscowDay } from './moscowTime';

export type When = 'now' | 'today' | 'tomorrow' | { date: string };
export type PassDuration = 'oneTime' | 'temporary' | 'permanent';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const TEMPORARY_DEFAULT_DAYS = 7;

interface Input {
  when: When;
  duration: PassDuration;
  /** passesLimitHoursOneTime / passesLimitHoursTemporary of the object; null — no limit. */
  limitHours: number | null;
  now: Date;
  /** Approval requests are not moved to "now" by the server, so they must start in the future. */
  forRequest?: boolean;
  /** Temporary pass only: the last day ("Y-m-d") picked by the resident; the window ends at its 23:59. */
  until?: string;
}

/**
 * startsAt/expiresAt for POST /v1/passes. The server does not default expiresAt and rejects windows longer
 * than the object limit (PASSES_LIMIT_HOURS), so the client computes it (spec §6.3 «Когда»).
 */
export function passWindow({ when, duration, limitHours, now, forRequest, until }: Input): { startsAt: Date | null; expiresAt: Date | null } {
  if (duration === 'permanent') return { startsAt: null, expiresAt: null };

  const nowMinute = new Date(Math.floor(now.getTime() / MINUTE) * MINUTE + (forRequest ? MINUTE : 0));
  let day: Date;
  if (when === 'now' || when === 'today') day = startOfMoscowDay(now);
  else if (when === 'tomorrow') day = startOfMoscowDay(now, 1);
  else day = startOfMoscowDay(parseServerDate(`${when.date} 12:00:00`)!);

  const startsAt = day.getTime() <= nowMinute.getTime() ? nowMinute : day;
  const limitEnd = limitHours ? new Date(startsAt.getTime() + limitHours * HOUR) : null;
  const pickedEnd = duration === 'temporary' && until ? endOfMoscowDay(parseServerDate(`${until} 12:00:00`)!) : null;
  const naturalEnd =
    duration === 'oneTime'
      ? endOfMoscowDay(startsAt)
      : pickedEnd && pickedEnd > startsAt
        ? pickedEnd
        : limitEnd && !pickedEnd
          ? limitEnd
          : endOfMoscowDay(startsAt, TEMPORARY_DEFAULT_DAYS - 1);

  const expiresAt = limitEnd && limitEnd < naturalEnd ? limitEnd : naturalEnd;
  return { startsAt, expiresAt };
}

/** The latest day a temporary pass may end on ("Y-m-d"), or null when the object sets no limit. */
export function lastAllowedDay(startsAt: Date | null, limitHours: number | null): string | null {
  if (!startsAt || !limitHours) return null;
  return formatServerDate(new Date(startsAt.getTime() + limitHours * HOUR)).slice(0, 10);
}
